import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, CheckCircle } from 'lucide-react';
import RichTextBlockEleve from './RichTextBlockEleve';

const COMPS_MATH = [
  { id: 1, nom: "Calculer" },
  { id: 2, nom: "Chercher" },
  { id: 3, nom: "Communiquer" },
  { id: 4, nom: "Raisonner" },
  { id: 5, nom: "Modéliser" },
  { id: 6, nom: "Représenter" },
];

const COMPS_FRA = [
  { id: 7, nom: "Comprendre et s'exprimer à l'oral" },
  { id: 8, nom: "Lire" },
  { id: 9, nom: "Écrire" },
  { id: 10, nom: "Comprendre le fonctionnement de la langue" },
  { id: 11, nom: "Acquérir des éléments de culture littéraire et artistique" },
];

const ModalCompetencesQCM = ({ session, matiere, onConfirm, onClose }) => {
  const competences = matiere === 'francais' ? COMPS_FRA : COMPS_MATH;

  // Filtrer : uniquement les questions qui ont un attendu/savoir_faire mais pas de compétence
  const questionsToAssign = useMemo(() => {
    const qs = session?.questions || [];
    return [...qs]
      .filter((q) => {
        const bq = q.banque_question;
        return (bq?.attendu || bq?.savoir_faire) && !bq?.competence;
      })
      .sort((a, b) => a.ordre - b.ordre);
  }, [session]);

  // Map : question_session_id → competence_id
  const [compMap, setCompMap] = useState({});

  const handleChange = (questionId, compId) => {
    setCompMap((prev) => {
      const next = { ...prev };
      if (compId) {
        next[questionId] = compId;
      } else {
        delete next[questionId];
      }
      return next;
    });
  };

  const assignedCount = Object.keys(compMap).length;
  const totalCount = questionsToAssign.length;
  const missingCount = totalCount - assignedCount;

  const handleConfirm = () => {
    const filtered = {};
    for (const [qId, cId] of Object.entries(compMap)) {
      if (cId) filtered[qId] = cId;
    }
    onConfirm(filtered);
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={(e) => e.stopPropagation()} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-blue-600 text-white p-4 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">Affecter les compétences</h3>
            <p className="text-white/70 text-xs mt-0.5">{session.titre}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/20 transition">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {questionsToAssign.length === 0 ? (
            <div className="text-center py-12 text-gray-500">Toutes les questions ont déjà une compétence</div>
          ) : (
            // Par question : numéro, énoncé rendu comme partout (maths, figures,
            // éléments additionnels : le même bloc que l'aperçu et les
            // résultats), attendu, compétence. Pas les choix de réponse : ils
            // ne servent pas à choisir une compétence, l'œil de la ligne les
            // montre (2026-09-05).
            questionsToAssign.map((q) => {
              const bq = q.banque_question;
              const hasComp = Boolean(compMap[q.id]);

              return (
                <div
                  key={q.id}
                  className={`p-4 rounded-xl border transition ${
                    hasComp ? 'border-emerald-200 bg-emerald-50/30' : 'border-blue-200 bg-blue-50/30'
                  }`}
                >
                  {/* Ligne header : badge + texte + indicateur */}
                  <div className="flex items-start gap-3">
                    {hasComp ? (
                      <CheckCircle size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                    ) : (
                      <div className="w-4 h-4 rounded-full bg-blue-300 shrink-0 mt-0.5" />
                    )}

                    <div className="flex-1 min-w-0">
                      {/* Q{n} + texte */}
                      <div className="flex items-start gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded-md bg-indigo-100 text-indigo-600 text-xs font-bold shrink-0">
                          Q{q.ordre}
                        </span>
                        <div className="text-sm font-medium min-w-0 flex-1">
                          <RichTextBlockEleve
                            texte={bq?.texte || ''}
                            elements={bq?.elements_additionnels}
                            theme="light"
                            imageMaxWidth="compact"
                          />
                        </div>
                      </div>

                      {/* Attendu / Savoir-faire */}
                      {(bq?.attendu_titre || bq?.savoir_faire_titre) && (
                        <p className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg mb-2 ml-7">
                          {bq.attendu_titre || bq.savoir_faire_titre}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Select compétence */}
                  <div className="mt-2 ml-7 pl-7">
                    <select
                      value={compMap[q.id] || ''}
                      onChange={(e) => handleChange(q.id, e.target.value ? Number(e.target.value) : null)}
                      className="w-full max-w-xs px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 focus:outline-none"
                    >
                      <option value="">-- Compétence --</option>
                      {competences.map((c) => (
                        <option key={c.id} value={c.id}>{c.nom}</option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-4 py-3 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-3 text-sm">
            <span className="text-gray-600">
              {assignedCount} / {totalCount} avec compétence
            </span>
            {missingCount > 0 && (
              <span className="flex items-center gap-1 text-blue-500 text-xs">
                <AlertTriangle size={14} />
                {missingCount} question{missingCount > 1 ? 's' : ''} sans compétence
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition"
            >
              Annuler
            </button>
            <button
              onClick={handleConfirm}
              disabled={assignedCount === 0}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                assignedCount > 0
                  ? 'bg-indigo-500 hover:bg-indigo-600 text-white shadow-md shadow-indigo-500/20'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              Valider et noter
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ModalCompetencesQCM;
